import type { FileMarksDto, MarkDto, MarkShape, MarkThreadDto } from '@nsb/core';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, errorText } from '../api.ts';
import { foldedWords, sheetPage } from './markupWords.ts';
import { PdfPages } from './PdfPages.tsx';
import { Button, ErrorLine, Seg } from './ui.tsx';
import { dateWords } from '../days.ts';

/**
 * The markup engine for anything shared with a client (Jason, 2026-10-02):
 * the file, the marks both sides have made on it (pins, boxes, circles,
 * freehand), numbered threads of comments and replies beside it, and the whole
 * history. Marks are measured 0..1 across and down the image, so they stay put
 * at any size. Nothing is ever changed in place: an edit is a new version, a
 * removal is withdrawn, and the history shows both.
 *
 * A comment resolved folds in place, never hidden (design study 29, fold A; Jason, 2026-10-28: "maybe it just folds the
 * comment up with a single line of the text ending in ... i dont want to lose the comments"): its number, the start of
 * its words (core `opening`, the timeline's own rule) and the word resolved, whole on a press; its mark stays on the
 * drawing, faint, drawn in full while it is open.
 */

type Tool = 'look' | 'pin' | 'box' | 'ellipse' | 'path' | 'note';

const TOOLS: [Tool, string][] = [
  ['look', 'Look'],
  ['pin', 'Pin'],
  ['box', 'Box'],
  ['ellipse', 'Circle'],
  ['path', 'Draw'],
  ['note', 'Note on the whole file'],
];

/**
 * The tools, as the markup page lays them out: a row of words, the one in
 * hand filled in. Help shows this same row (Reviews, How this works), so what
 * a client is told to press is what they will find.
 */
export function MarkupTools({ tools = TOOLS, active = 'look', onPick }: { tools?: readonly (readonly [Tool, string])[]; active?: Tool; onPick?: (t: Tool) => void }) {
  return (
    <div className="mb-3 flex flex-wrap gap-2" role="toolbar" aria-label="Mark up">
      {tools.map(([t, label]) => (
        <button
          key={t}
          type="button"
          aria-pressed={active === t}
          tabIndex={onPick ? undefined : -1}
          onClick={onPick ? () => onPick(t) : undefined}
          className={`min-h-10 rounded-[2px] border px-3 text-[0.8125rem] transition-colors duration-[120ms] ${active === t ? 'border-ink bg-ink text-paper' : 'border-rule-strong text-ink-2 hover:border-ink hover:text-ink'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const INK = { client: '#b66f48', contractor: '#596e7d', selected: '#20231f', resolved: '#aaa69c' };

function hostOf(url: string | null): string {
  try {
    return url ? new URL(url).hostname : 'A link';
  } catch {
    return 'A link';
  }
}
const when = (iso: string) => dateWords(iso, 'time');

export function Markup({
  projectId,
  data,
  onChange,
  thread = null,
  earlier = null,
}: {
  projectId: string;
  data: FileMarksDto;
  onChange: (d: FileMarksDto) => void;
  /** A comment to open on: picked out on the drawing and brought into view (from a link to it, "?thread="). */
  thread?: string | null;
  /**
   * The sheet this one revises, with its marks (design study 29): under it on a wide screen (P-38), read only, and a switch
   * between the two on a phone, so the two are compared on one page; its comments listed under this sheet's, each a line.
   */
  earlier?: FileMarksDto | null;
}) {
  const isImage = data.file.viewUrl !== null;
  // A PDF stored here (not a link), shown as its pages from the file as it came (L-35).
  const pdf = !isImage && data.file.kind === 'file' && data.file.contentType === 'application/pdf' ? data.file.originalUrl : null;
  const [tool, setTool] = useState<Tool>('look');
  const [draft, setDraft] = useState<MarkShape | 'note' | null>(null);
  const [drawing, setDrawing] = useState<MarkShape | null>(null);
  const [selected, setSelected] = useState<string | null>(thread);
  // Resolved comments opened from their folded line (fold A); one linked to opens.
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(() => new Set(thread ? [thread] : []));
  const isOpen = (t: MarkThreadDto) => !t.resolved || unfolded.has(t.threadId) || selected === t.threadId;
  const pick = (threadId: string) => {
    setSelected(threadId);
    setUnfolded((u) => new Set(u).add(threadId));
  };
  const toggle = (threadId: string) => {
    if (unfolded.has(threadId) || selected === threadId) {
      setUnfolded((u) => {
        const next = new Set(u);
        next.delete(threadId);
        return next;
      });
      if (selected === threadId) setSelected(null);
    } else pick(threadId);
  };
  // The earlier sheet under this one on a wide screen, unless put away, each the drawing column's whole width (P-38: "these
  // images need to take up as much space as possible ... revised drawings are at the top"); on a phone, one at a time.
  const [besideEarlier, setBesideEarlier] = useState(true);
  const [phoneSheet, setPhoneSheet] = useState<'this' | 'earlier'>('this');
  useEffect(() => {
    if (!thread) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.getElementById(`thread-${thread}`)?.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
  }, [thread]);
  const [showHistory, setShowHistory] = useState(false);
  const surface = useRef<HTMLDivElement>(null);
  const start = useRef<[number, number] | null>(null);

  const at = (e: React.PointerEvent): [number, number] => {
    const r = surface.current!.getBoundingClientRect();
    return [Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), Math.min(1, Math.max(0, (e.clientY - r.top) / r.height))];
  };
  const down = (e: React.PointerEvent) => {
    if (tool === 'look' || tool === 'note' || draft) return;
    e.preventDefault();
    surface.current!.setPointerCapture(e.pointerId);
    const [x, y] = at(e);
    start.current = [x, y];
    if (tool === 'pin') {
      setDraft({ type: 'pin', x, y });
      setTool('look');
      start.current = null;
    } else if (tool === 'path') setDrawing({ type: 'path', points: [[x, y]] });
    else setDrawing({ type: tool, x, y, w: 0, h: 0 });
  };
  const move = (e: React.PointerEvent) => {
    if (!start.current || !drawing) return;
    const [x, y] = at(e);
    if (drawing.type === 'path') {
      const last = drawing.points.at(-1)!;
      if (Math.hypot(x - last[0], y - last[1]) > 0.004) setDrawing({ type: 'path', points: [...drawing.points, [x, y]] });
    } else if (drawing.type === 'box' || drawing.type === 'ellipse') {
      const [sx, sy] = start.current;
      setDrawing({ type: drawing.type, x: Math.min(sx, x), y: Math.min(sy, y), w: Math.abs(x - sx), h: Math.abs(y - sy) });
    }
  };
  const up = () => {
    if (!drawing) return;
    const big = drawing.type === 'path' ? drawing.points.length > 2 : drawing.type === 'box' || drawing.type === 'ellipse' ? drawing.w > 0.01 && drawing.h > 0.01 : true;
    if (big) {
      setDraft(drawing);
      setTool('look');
    }
    setDrawing(null);
    start.current = null;
  };

  const threads = data.threads;
  const comparing = earlier !== null && earlier.file.viewUrl !== null && isImage;

  return (
    // The comments a narrower column, the drawing the rest (P-38: "comments can shrink a little").
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0">
        {earlier ? (
          <div className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[0.9375rem] text-ink-2">
            <span>
              Revises{' '}
              <Link to={sheetPage(projectId, earlier.file.id)} className="link text-ink">
                {earlier.file.name}
              </Link>
              {earlier.threads.length ? ` · ${earlier.threads.length} comment${earlier.threads.length === 1 ? '' : 's'} there` : ''}
            </span>
            {comparing ? (
              <button type="button" className="link hidden min-h-11 hover:text-ink md:inline" aria-pressed={besideEarlier} onClick={() => setBesideEarlier(!besideEarlier)}>
                {besideEarlier ? 'Hide the earlier sheet' : 'Show the earlier sheet under it'}
              </button>
            ) : null}
          </div>
        ) : null}
        {comparing ? (
          <div className="mb-3 md:hidden">
            <Seg value={phoneSheet} options={['earlier', 'this'] as const} words={(o) => (o === 'earlier' ? 'Earlier' : 'This sheet')} onPick={(o) => o && setPhoneSheet(o)} label="Which sheet" keep />
          </div>
        ) : null}
        {data.locked ? (
          <p className="mb-3 max-w-2xl border-l-2 border-moss pl-3 text-[0.9375rem] text-ink-2">
            Everyone asked has reviewed this iteration, so it is closed to comments. The comments stay here to read{data.canResolve ? '; open it again from Design reviews if there is more to add' : ''}.
          </p>
        ) : (
        // The tools mark this sheet: on a phone showing the earlier one, they wait until it is back.
        <div className={comparing && phoneSheet === 'earlier' ? 'hidden md:block' : ''}>
        <MarkupTools
          tools={TOOLS.filter(([t]) => isImage || t === 'look' || t === 'note')}
          active={tool}
          onPick={(t) => {
            setDraft(t === 'note' ? 'note' : null);
            setTool(t === 'note' ? 'look' : t);
          }}
        />
        </div>
        )}
        {tool !== 'look' ? <p className="meta mb-2">{tool === 'pin' ? 'Tap where it is.' : 'Drag across the image.'}</p> : null}
        <div className={comparing && besideEarlier ? 'md:space-y-6' : ''}>
        <div className={`min-w-0 ${comparing && phoneSheet === 'earlier' ? 'hidden md:block' : ''}`}>
        {comparing && besideEarlier ? <p className="label mb-1 hidden md:block">This sheet</p> : null}
        {isImage ? (
          <div
            ref={surface}
            className="relative select-none overflow-hidden rounded-[2px] bg-inset"
            style={{ aspectRatio: `${data.file.width ?? 3} / ${data.file.height ?? 2}`, touchAction: tool === 'look' ? 'auto' : 'none', cursor: tool === 'look' ? 'default' : 'crosshair' }}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
          >
            <img src={data.file.viewUrl!} alt={data.file.name} draggable={false} className="pointer-events-none absolute inset-0 h-full w-full" />
            <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
              {data.threads.map((t) => (t.root.shape && t.root.shape.type !== 'pin' ? <Shape key={t.threadId} shape={t.root.shape} color={colorOf(t, selected)} faint={!isOpen(t)} /> : null))}
              {drawing ? <Shape shape={drawing} color={INK.selected} dashed /> : null}
              {draft && draft !== 'note' && draft.type !== 'pin' ? <Shape shape={draft} color={INK.selected} dashed /> : null}
            </svg>
            {threads.map((t) => {
              const p = anchor(t.root.shape);
              return p ? (
                <button
                  key={t.threadId}
                  type="button"
                  aria-label={`Mark ${t.number}${t.resolved ? ', resolved' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    pick(t.threadId);
                    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    document.getElementById(`thread-${t.threadId}`)?.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
                  }}
                  onPointerDown={(e) => e.stopPropagation()}
                  // A 44px target around a 28px mark (style guide §20.13).
                  className="absolute flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                  style={{ left: `${p[0] * 100}%`, top: `${p[1] * 100}%` }}
                >
                  <span
                    className="num flex h-7 w-7 items-center justify-center rounded-full border-2 border-paper text-[0.75rem] text-paper shadow-[0_1px_2px_rgba(0,0,0,0.25)]"
                    style={{ background: colorOf(t, selected), opacity: isOpen(t) ? 1 : FAINT }}
                  >
                    {t.number}
                  </span>
                </button>
              ) : null;
            })}
            {draft && draft !== 'note' ? (
              <span
                className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-paper bg-ink motion-safe:animate-pulse"
                style={{ left: `${anchor(draft)![0] * 100}%`, top: `${anchor(draft)![1] * 100}%` }}
              />
            ) : null}
          </div>
        ) : pdf ? (
          // L-35: a PDF shown as its pages, one after another, where its box was; never a viewer of its own.
          <PdfPages url={pdf} name={data.file.name} />
        ) : (
          <div className="rounded-[2px] border border-rule bg-sheet p-6">
            <p className="font-display text-[1.25rem]">{data.file.name}</p>
            <p className="meta mt-1">{data.file.kind === 'link' ? hostOf(data.file.url) : data.file.contentType === 'application/pdf' ? 'PDF' : data.file.contentType}</p>
            <a className="link mt-3 inline-block" href={data.file.url ?? data.file.originalUrl ?? undefined} target="_blank" rel="noreferrer">
              {data.file.kind === 'link' ? 'Open it where it is' : data.file.contentType === 'application/pdf' ? 'Open it' : 'Download it'}
            </a>
            <p className="mt-3 text-sm text-ink-2">
              {data.file.contentType === 'application/pdf'
                ? // P-36: a PDF added to a design review is split into plates, each page a picture to mark up.
                  'Comments on it go beside it. Added to a design review, each of its pages becomes a plate there, to mark up.'
                : 'Comments on it go beside it.'}
            </p>
          </div>
        )}
        <p className="meta mt-2 flex flex-wrap gap-x-4">
          {data.file.originalUrl ? (
            <a className="link" href={data.file.originalUrl} target="_blank" rel="noreferrer">
              The file as it came
            </a>
          ) : null}
          {isImage ? <span>Tap a number to see its comments.</span> : null}
          {pdf ? <span>Comments on it go beside it. Added to a design review, each of its pages becomes a plate there, to mark up.</span> : null}
        </p>
        </div>
        {/* The sheet it revises under it, newest first (P-38). */}
        {comparing ? (
          <div className={`min-w-0 ${phoneSheet === 'earlier' ? '' : 'hidden'} ${besideEarlier ? 'md:block' : 'md:hidden'}`}>
            <EarlierSheet projectId={projectId} data={earlier!} />
          </div>
        ) : null}
        </div>
      </div>

      <aside className="min-w-0">
        {draft ? <DraftMark projectId={projectId} fileId={data.file.id} shape={draft === 'note' ? null : draft} onDone={(d) => (d && onChange(d), setDraft(null))} /> : null}
        <div className="border-b border-ink/40 pb-2">
          <h2 className="text-[1.25rem]">Comments</h2>
        </div>
        {threads.length === 0 ? (
          <p className="py-4 text-ink-3">{data.locked ? 'Nobody commented on this one.' : 'No comments yet. Pick a tool and mark the image, or leave a note.'}</p>
        ) : null}
        <ol className="divide-y divide-rule">
          {threads.map((t) => (
            <Thread
              key={t.threadId}
              projectId={projectId}
              fileId={data.file.id}
              thread={t}
              canResolve={data.canResolve}
              locked={data.locked}
              selected={selected === t.threadId}
              onSelect={() => pick(t.threadId)}
              folded={!isOpen(t)}
              onToggle={() => toggle(t.threadId)}
              onChange={onChange}
            />
          ))}
        </ol>
        {earlier && earlier.threads.length ? <EarlierComments projectId={projectId} data={earlier} /> : null}
        <div className="mt-6 border-t border-rule pt-3">
          <button type="button" className="link min-h-11 text-sm text-ink-2 hover:text-ink" onClick={() => setShowHistory(!showHistory)} aria-expanded={showHistory}>
            {showHistory ? 'Hide the history' : `The history (${data.history.length})`}
          </button>
          {showHistory ? (
            <ol className="mt-2 space-y-2">
              {data.history.map((h, i) => (
                <li key={i} className="text-[0.8125rem] text-ink-2">
                  <span className="meta">{when(h.at)}</span> {h.who} {WHAT[h.what]} {h.number ? <span className="num">#{h.number}</span> : null}
                  {h.body && h.what !== 'withdrew' ? <span className="block text-ink">“{h.body}”</span> : null}
                  {h.before !== null && h.what === 'edited' ? <span className="block text-ink-3 line-through">“{h.before}”</span> : null}
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      </aside>
    </div>
  );
}

const WHAT: Record<FileMarksDto['history'][number]['what'], string> = {
  marked: 'marked',
  replied: 'replied on',
  edited: 'edited',
  withdrew: 'withdrew',
  resolved: 'resolved',
  reopened: 'reopened',
};

function colorOf(t: MarkThreadDto, selected: string | null): string {
  if (t.threadId === selected) return INK.selected;
  if (t.resolved) return INK.resolved;
  return INK[t.root.authorRole];
}

/** Where a mark's number sits: on a pin, at a shape's top-left, at a stroke's start. */
function anchor(shape: MarkShape | null): [number, number] | null {
  if (!shape) return null;
  switch (shape.type) {
    case 'pin':
    case 'box':
    case 'ellipse':
      return [shape.x, shape.y];
    case 'path':
      return shape.points[0] ?? null;
    case 'arrow':
      return [shape.x1, shape.y1];
  }
}

/** How faint a resolved mark is on the drawing while its comment is folded. */
const FAINT = 0.5;

function Shape({ shape, color, dashed = false, faint = false }: { shape: MarkShape; color: string; dashed?: boolean; faint?: boolean }) {
  const stroke = {
    stroke: color,
    strokeWidth: 2.5,
    strokeOpacity: faint ? FAINT : 1,
    fill: 'none',
    vectorEffect: 'non-scaling-stroke' as const,
    strokeDasharray: dashed ? '6 4' : undefined,
  };
  if (shape.type === 'box') return <rect x={shape.x} y={shape.y} width={shape.w} height={shape.h} {...stroke} />;
  if (shape.type === 'ellipse') return <ellipse cx={shape.x + shape.w / 2} cy={shape.y + shape.h / 2} rx={shape.w / 2} ry={shape.h / 2} {...stroke} />;
  if (shape.type === 'path') return <polyline points={shape.points.map(([x, y]) => `${x},${y}`).join(' ')} strokeLinecap="round" strokeLinejoin="round" {...stroke} />;
  if (shape.type === 'arrow') return <line x1={shape.x1} y1={shape.y1} x2={shape.x2} y2={shape.y2} {...stroke} />;
  return null;
}

/** The words for a new mark (or a note on the whole file), before it is saved. */
function DraftMark({ projectId, fileId, shape, onDone }: { projectId: string; fileId: string; shape: MarkShape | null; onDone: (d: FileMarksDto | null) => void }) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      onDone(await api<FileMarksDto>(`/projects/${projectId}/reviews/files/${fileId}/marks`, { body: { shape, body: body.trim() || null } }));
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };
  return (
    <div className="mb-6 border border-ink p-4">
      <p className="label mb-2">{shape ? 'Your mark' : 'A note on the whole file'}</p>
      <textarea autoFocus rows={3} className="field resize-y" value={body} onChange={(e) => setBody(e.target.value)} placeholder="What about it?" />
      <div className="mt-3 flex gap-5">
        <Button busy={busy} disabled={!shape && !body.trim()} onClick={() => void save()}>
          Save
        </Button>
        <Button tone="quiet" onClick={() => onDone(null)}>
          Cancel
        </Button>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

export function Thread({
  projectId,
  fileId,
  thread: t,
  canResolve,
  locked,
  selected,
  onSelect,
  folded = false,
  onToggle,
  onChange,
}: {
  projectId: string;
  fileId: string;
  thread: MarkThreadDto;
  canResolve: boolean;
  /** The iteration is reviewed: what was said can be read, and resolved, but not added to. */
  locked: boolean;
  selected: boolean;
  onSelect: () => void;
  /** Resolved and folded in place (fold A): one line, its number, the start of its words and the word resolved. */
  folded?: boolean;
  /** Opens a resolved comment from its folded line, or folds it again. */
  onToggle?: () => void;
  onChange: (d: FileMarksDto) => void;
}) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<FileMarksDto>) => {
    setBusy(true);
    setErr(null);
    try {
      onChange(await fn());
      return true;
    } catch (e) {
      setErr(errorText(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  if (folded) {
    return (
      <li id={`thread-${t.threadId}`}>
        <button type="button" className="flex min-h-11 w-full items-baseline gap-3 py-2 text-left hover:text-ink" aria-expanded={false} onClick={onToggle}>
          <ResolvedNumber n={t.number} />
          <span className="min-w-0 flex-1 truncate text-ink-2">{foldedWords(t)}</span>
          <span className="label shrink-0">resolved</span>
        </button>
      </li>
    );
  }
  // Resolved and opened from its line: the line above it says who resolved it, and folds it again.
  const opened = t.resolved && onToggle !== undefined;
  return (
    <li id={`thread-${t.threadId}`} className={`py-4 ${selected ? 'bg-inset/60 -mx-2 px-2' : ''} ${t.resolved && !opened ? 'opacity-70' : ''}`} onClick={onSelect}>
      {opened ? (
        <button
          type="button"
          className="mb-1 flex min-h-11 w-full items-baseline gap-3 text-left hover:text-ink"
          aria-expanded
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <ResolvedNumber n={t.number} />
          <span className="min-w-0 flex-1 text-sm text-ink-3">
            {t.resolvedBy ? `Resolved by ${t.resolvedBy}${t.resolvedAt ? `, ${dateWords(t.resolvedAt, 'dayOrDate')}` : ''}` : 'Resolved'}
          </span>
          <span className="label shrink-0">Fold it</span>
        </button>
      ) : null}
      <div className="flex items-baseline gap-3">
        {opened ? <span className="w-6 shrink-0" aria-hidden="true" /> : <button
          type="button"
          aria-label={`Show comment ${t.number} on the drawing`}
          aria-pressed={selected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className="hit num inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-[0.75rem] text-paper"
          style={{ background: t.resolved ? INK.resolved : INK[t.root.authorRole] }}
        >
          {t.number}
        </button>}
        <span className="min-w-0 flex-1">
          <MarkBody projectId={projectId} mark={t.root} locked={locked} onChange={onChange} />
        </span>
      </div>
      {t.replies.length ? (
        <ul className="mt-2 space-y-2 border-l border-rule pl-4 ml-3">
          {t.replies.map((r) => (
            <li key={r.markId}>
              <MarkBody projectId={projectId} mark={r} locked={locked} onChange={onChange} />
            </li>
          ))}
        </ul>
      ) : null}
      <div className="ml-9 mt-1 flex flex-wrap gap-x-5">
        {t.resolved && !opened ? <span className="label self-center">resolved</span> : null}
        {!locked ? (
          <button type="button" className="link min-h-11 text-sm text-ink-2 hover:text-ink" onClick={() => setReplying(!replying)}>
            Reply
          </button>
        ) : null}
        {canResolve ? (
          <button
            type="button"
            className="link min-h-11 text-sm text-ink-2 hover:text-ink"
            disabled={busy}
            onClick={() => void run(() => api<FileMarksDto>(`/projects/${projectId}/reviews/threads/${t.threadId}/state`, { body: { resolved: !t.resolved } }))}
          >
            {t.resolved ? 'Reopen' : 'Resolve'}
          </button>
        ) : null}
      </div>
      {replying && !locked ? (
        <div className="ml-9 mt-1">
          <textarea autoFocus rows={2} className="field resize-y" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Your reply" />
          <div className="mt-2 flex gap-5">
            <Button
              tone="secondary"
              busy={busy}
              disabled={!reply.trim()}
              onClick={() =>
                void run(() => api<FileMarksDto>(`/projects/${projectId}/reviews/files/${fileId}/marks`, { body: { threadId: t.threadId, body: reply.trim() } })).then(
                  (ok) => ok && (setReply(''), setReplying(false)),
                )
              }
            >
              Reply
            </Button>
            <Button tone="quiet" onClick={() => setReplying(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      <ErrorLine error={err} />
    </li>
  );
}

/** One mark or reply: who, when, what; its author can change the words or withdraw it. */
function MarkBody({ projectId, mark: m, locked = false, onChange }: { projectId: string; mark: MarkDto; locked?: boolean; onChange: (d: FileMarksDto) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(m.body ?? '');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const revise = async (change: { body?: string | null; withdrawn?: boolean }) => {
    setBusy(true);
    setErr(null);
    try {
      onChange(await api<FileMarksDto>(`/projects/${projectId}/reviews/marks/${m.markId}`, { body: change }));
      setEditing(false);
      setConfirming(false);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="meta">
        {m.mine ? 'You' : m.authorName}
        {m.authorRole === 'contractor' ? ' · NSB' : ''} · {when(m.createdAt)}
        {m.version > 1 ? ' · edited' : ''}
      </p>
      {editing ? (
        <div className="mt-1">
          <textarea
            autoFocus
            rows={2}
            className="field resize-y"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={(e) => e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)}
          />
          <div className="mt-2 flex gap-5">
            <Button tone="secondary" busy={busy} onClick={() => void revise({ body: text.trim() || null })}>
              Save
            </Button>
            <Button tone="quiet" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : m.body ? (
        <p className="mt-0.5 whitespace-pre-line">{m.body}</p>
      ) : null}
      {m.mine && !editing && !locked ? (
        <span className="flex gap-4">
          <button type="button" className="link min-h-9 text-[0.8125rem] text-ink-3 hover:text-ink" onClick={() => setEditing(true)}>
            Edit
          </button>
          <button type="button" className="link min-h-9 text-[0.8125rem] text-ink-3 hover:text-alert" onClick={() => setConfirming(true)}>
            Withdraw
          </button>
        </span>
      ) : null}
      {confirming ? (
        <span className="flex flex-wrap items-center gap-4 text-sm">
          Withdraw it? It stays in the history.
          <Button tone="danger" busy={busy} onClick={() => void revise({ withdrawn: true })}>
            Yes
          </Button>
          <Button tone="quiet" onClick={() => setConfirming(false)}>
            No
          </Button>
        </span>
      ) : null}
      <ErrorLine error={err} />
    </div>
  );
}

/** A resolved comment's number: drawn, not filled, so it reads beside the open ones. */
function ResolvedNumber({ n }: { n: number }) {
  return <span className="num inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full border border-rule-strong px-1.5 text-[0.75rem] text-ink-2">{n}</span>;
}

/**
 * The sheet this one revises, beside it (design study 29): its picture and every mark on it, faint, read only; a number
 * opens its comment on its own page.
 */
function EarlierSheet({ projectId, data }: { projectId: string; data: FileMarksDto }) {
  return (
    <div className="min-w-0">
      <p className="label mb-1 truncate">Earlier · {data.file.name}</p>
      <div className="relative select-none overflow-hidden rounded-[2px] bg-inset" style={{ aspectRatio: `${data.file.width ?? 3} / ${data.file.height ?? 2}` }}>
        <img src={data.file.viewUrl!} alt={`The earlier sheet, ${data.file.name}`} draggable={false} className="pointer-events-none absolute inset-0 h-full w-full" />
        <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true">
          {data.threads.map((t) => (t.root.shape && t.root.shape.type !== 'pin' ? <Shape key={t.threadId} shape={t.root.shape} color={INK.resolved} faint={t.resolved} /> : null))}
        </svg>
        {data.threads.map((t) => {
          const p = anchor(t.root.shape);
          return p ? (
            <Link
              key={t.threadId}
              to={sheetPage(projectId, data.file.id, t.threadId)}
              aria-label={`Comment ${t.number} on the earlier sheet${t.resolved ? ', resolved' : ''}`}
              className="absolute flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center"
              style={{ left: `${p[0] * 100}%`, top: `${p[1] * 100}%` }}
            >
              <span className="num flex h-7 w-7 items-center justify-center rounded-full border-2 border-paper text-[0.75rem] text-paper" style={{ background: INK.resolved, opacity: t.resolved ? FAINT : 1 }}>
                {t.number}
              </span>
            </Link>
          ) : null;
        })}
      </div>
      <p className="meta mt-1">Read only. Tap a number to open its comment.</p>
    </div>
  );
}

/** The earlier sheet's comments under this sheet's, each a line that opens it there. */
function EarlierComments({ projectId, data }: { projectId: string; data: FileMarksDto }) {
  return (
    <div className="mt-6">
      <p className="label mb-1">On the earlier sheet</p>
      <ol className="divide-y divide-rule border-y border-rule">
        {data.threads.map((t) => (
          <li key={t.threadId}>
            <Link to={sheetPage(projectId, data.file.id, t.threadId)} className="flex min-h-11 items-baseline gap-3 py-2 hover:text-ink">
              <ResolvedNumber n={t.number} />
              <span className="min-w-0 flex-1 truncate text-ink-2">{foldedWords(t)}</span>
              {t.resolved ? <span className="label shrink-0">resolved</span> : null}
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}
